import {
  Alert,
  Backdrop,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";

const cancellationReasons = [
  "Found another mover",
  "Moving date changed",
  "No longer moving",
  "Price too high",
  "Other",
];

interface CancelMoveModalProps {
  open: boolean;
  onClose: () => void;
  onCancel: (reason: string, details?: string) => void;
  cancellationPeriod: number | null;
  currentDate: any;
}
const CancelMoveModal = ({
  open,
  onClose,
  onCancel,
  cancellationPeriod,
  currentDate,
}: CancelMoveModalProps) => {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState("");
  const canCancel = () => {
    if (
      !currentDate ||
      cancellationPeriod === null ||
      cancellationPeriod === undefined
    ) {
      return true;
    }

    const moveDate = new Date(currentDate);
    moveDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const timeDiff = moveDate.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
    return daysDiff > cancellationPeriod;
  };
  const handleCancel = () => {
    setIsLoading(true);
    setLoadingMessage("Cancelling the move......");
    setError(null);

    setTimeout(async () => {
      try {
        if (!canCancel()) {
          setError(
            `Move cannot be cancelled within ${cancellationPeriod} days of the move date.`
          );
          setIsLoading(false);
          return;
        }
        await onCancel(reason, details);
        onClose();
        window.location.reload();
      } catch (err: any) {
        setError(err.message || "Failed to cancel move");
        setIsLoading(false);
      }
    }, 1500);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {isLoading && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
          onClick={() => {
            setIsLoading(true);
          }}
        >
          <Box sx={{ width: "50%" }}>
            <LinearProgress />
            <Typography
              fontSize={16}
              fontWeight={500}
              textAlign="center"
              mt={1}
            >
              {loadingMessage}
            </Typography>
          </Box>
        </Backdrop>
      )}
      <DialogTitle>Cancel Move</DialogTitle>
      <DialogContent>
        {/* {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )} */}
        {!canCancel() && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Move date cannot be modified within {cancellationPeriod} days of the
            move date.
          </Alert>
        )}
        <Typography variant="body1" sx={{ mb: 2 }}>
          Are you sure you want to cancel this move?
        </Typography>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Reason for cancellation</InputLabel>
          <Select
            value={reason}
            label="Reason for cancellation"
            onChange={(e) => setReason(e.target.value)}
            disabled={isLoading || !canCancel()}
          >
            {cancellationReasons.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {reason === "Other" && (
          <TextField
            label="Please specify"
            fullWidth
            multiline
            rows={3}
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            sx={{ mb: 2 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>No, Keep Move</Button>
        <Button
          onClick={handleCancel}
          variant="contained"
          color="error"
          disabled={!reason || (reason === "Other" && !details)}
        >
          Yes, Cancel Move
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CancelMoveModal;
